<template>
  <content-dialog
    ref="dialog"
    v-model="dialogOpen"
    fullscreen-mobile
    no-backdrop-dismiss
    width="800px"
    max-height="80vh"
    :card-visible="!!editObject"
    :title="(mode === 'edit' ? 'Edit' : 'Add') + ' Appointment Type'"
    :tags="[helpCodes.appointmentTypes]"
    @hide="$emit('close')"
  >
    <template v-if="editObject">
      <q-list dense>
        <sticky-header>Type Details</sticky-header>

        <detail-item
          :name-spacing="nameSpacing"
          without-icon
          with-input
          name="Name"
          required
        >
          <q-input
            v-model="editObject.name"
            dense
            outlined
            clearable
            :error="!!errors['name']"
            :error-message="errors['name'] ? errors['name'][0] : null"
            hide-bottom-space
            @update:model-value="onInputName"
          />
        </detail-item>

        <detail-item
          :name-spacing="nameSpacing"
          without-icon
          with-input
          name="Shorthand Name"
        >
          <q-input
            v-model="editObject.shorthand_name"
            dense
            outlined
            clearable
            hint="A shorter variant to the name"
            hide-bottom-space
            counter
            :maxlength="shorthandNameLimit"
            :error="!!errors['shorthand_name']"
            :error-message="errors['shorthand_name'] ? errors['shorthand_name'][0] : null"
            @update:model-value="autoShorthandName = false"
          />
        </detail-item>

        <detail-item
          :name-spacing="nameSpacing"
          without-icon
          with-input
          name="Colour"
        >
          <color-field
            v-model="editObject.color"
          />
        </detail-item>

        <detail-item
          :name-spacing="nameSpacing"
          without-icon
          with-input
          tag="label"
          name="Must Be Staffed"
        >
          <q-checkbox
            v-model="editObject.must_be_staffed"
            label="At least one staff member must be added to new appointments which use this type"
          />
        </detail-item>

        <q-separator class="q-my-md" inset />

        <detail-item
          :name-spacing="nameSpacing"
          without-icon
          with-input
          name="Client-visible Title"
        >
          <q-input
            v-model="editObject.client_title"
            dense
            outlined
            hint="The visible name of this type which clients may see on relevant communications"
            :error="!!errors['client_title']"
            :error-message="errors['client_title'] ? errors['client_title'][0] : null"
            hide-bottom-space
          />
        </detail-item>

        <detail-item
          :name-spacing="nameSpacing"
          without-icon
          with-input
          name="Client-visible Description"
        >
          <q-input
            v-model="editObject.client_description"
            dense
            outlined
            autogrow
            hint="The visible description of this type which clients may see on relevant communications"
            :error="!!errors['client_description']"
            class="q-mb-sm"
            :error-message="errors['client_description'] ? errors['client_description'][0] : null"
            hide-bottom-space
          />
        </detail-item>
      </q-list>

      <q-list class="q-mt-lg" dense>
        <sticky-header>Default Appointment Details</sticky-header>

        <detail-item
          :name-spacing="nameSpacing"
          without-icon
          with-input
          name="Appointment Title"
        >
          <q-input
            v-model="editObject.title"
            dense
            hint="Title will be prefilled on new appointments. Leaving this blank will prefill the client's name"
            outlined
            hide-bottom-space
            :error="!!errors['title']"
            :error-message="errors['title'] ? errors['title'][0] : null"
          />
        </detail-item>

        <detail-item
          :name-spacing="nameSpacing"
          without-icon
          with-input
          name="Appointment Description"
        >
          <q-input
            v-model="editObject.description"
            dense
            hint="Description will be prefilled on new appointments"
            outlined
            autogrow
            hide-bottom-space
            :error="!!errors['description']"
            :error-message="errors['description'] ? errors['description'][0] : null"
          />
        </detail-item>

        <detail-item
          :name-spacing="nameSpacing"
          without-icon
          with-input
          name="Time Allocation"
        >
          <minute-picker
            v-model="editObject.time_allocation"
            dense
            hint="Default time allocation to apply to new appointments with this type"
            mobile-label="Select Duration"
            limit-offset="after"
            :options="[15, 30, 60, 120, 300, 480]"
            :with-suffix-label="false"
            :min-offset="15"
            :error="!!errors['time_allocation']"
            :error-message="errors['time_allocation'] ? errors['time_allocation'][0] : null"
          />
        </detail-item>

        <detail-item
          :name-spacing="nameSpacing"
          :with-input="!(editObject.recursion_rule && recurringRule)"
          without-icon
          name="Recurring Rule"
        >
          <div v-if="editObject.recursion_rule && recurringRule">
            <span>Rule: </span>
            <span> {{ recurringRule.toText() }}</span>
          </div>
          <q-btn
            size="12px"
            :label="(recurringRule ? 'Update Rule' : 'Create Recurring Rule')"
            color="green"
            :icon="recurringRule ? 'mdi-pencil' : 'mdi-plus'"
            class="relative-position"
            style="margin-top: 3px;"
            @click="$refs.rruleDialog.open(editObject.recursion_rule ? editObject.recursion_rule.value : null)"
          />
        </detail-item>
      </q-list>

      <q-list class="q-mt-lg" dense>
        <sticky-header>Default Reminders</sticky-header>

        <detail-item
          :name-spacing="nameSpacing"
          without-icon
          with-input
          name="Client Reminders Beforehand"
        >
          <EditableOffsetList
            v-model="editObject.client_reminder_offset"
            :default-offset="-1440"
            :increment-offset="-1440"
            :limit="CLIENT_REMINDER_LIMIT"
            :error="!!errors['client_reminder_offset']"
            :error-message="errors['client_reminder_offset'] ? errors['client_reminder_offset'][0] : null"
          />
        </detail-item>

        <detail-item
          :name-spacing="nameSpacing"
          without-icon
          with-input
          name="Staff Reminders Beforehand"
        >
          <EditableOffsetList
            v-model="editObject.staff_reminder_offset"
            :default-offset="-60"
            :increment-offset="-60"
            :limit="STAFF_REMINDER_LIMIT"
            :error="!!errors['staff_reminder_offset']"
            :error-message="errors['staff_reminder_offset'] ? errors['staff_reminder_offset'][0] : null"
          />
        </detail-item>

        <detail-item
          :name-spacing="nameSpacing"
          without-icon
          with-input
          name="Unscheduled Reminders Beforehand"
        >
          <EditableOffsetList
            v-model="editObject.unscheduled_reminder_offset"
            :default-offset="-20160"
            :increment-offset="-1440"
            :limit="UNSCHEDULED_REMINDER_LIMIT"
            :error="!!errors['unscheduled_reminder_offset']"
            :error-message="errors['unscheduled_reminder_offset'] ? errors['unscheduled_reminder_offset'][0] : null"
          />
        </detail-item>
      </q-list>

      <q-list class="q-mt-lg">
        <sticky-header>Default Line Items</sticky-header>
        <q-item-label
          class="q-px-md q-pb-sm text-bold"
        >
          Prefill line items for new quotations and invoices which inherit this appointment type.
        </q-item-label>

        <q-list separator>
          <DefaultLineItem
            v-for="(row, key) in editObject.inventory"
            :key="'data-' + key"
            :row="row"
            :errors="getErrorsForProp(errors, `inventory.${key}.*`)"
            @remove="removeDataField(key)"
          />
          <clickable-item
            :label="'Add ' + (editObject.inventory.length > 0 ? 'another' : '') + ' line item...'"
            @click="addDataField"
          />
        </q-list>
      </q-list>
    </template>

    <template #bottom>
      <q-toolbar v-if="!!editObject">
        <q-space />
        <q-btn
          color="green"
          icon-right="done"
          label="Save"
          @click="saveType"
        />
      </q-toolbar>
    </template>
  </content-dialog>

  <rrule-dialog
    v-if="!!editObject"
    ref="rruleDialog"
    @save="onSaveRRule"
    @remove="editObject.recursion_rule = null"
  >
    <q-item-wrapper dense>
      <q-item-label class="text-bold q-pb-sm">
        Mark recurring appointments as:
      </q-item-label>
      <div class="q-mb-sm">
        <q-btn-toggle
          v-model="editObject.recurring_status"
          :options="availableRecurringStatusOptions"
          no-caps
          size="12px"
        />
      </div>
    </q-item-wrapper>
  </rrule-dialog>
</template>

<script>
import EditableOffsetList from 'components/partials/communications/reminders/EditableOffsetList'
import DefaultLineItem from 'components/app/settings/company/appointmentsTypes/partials/DefaultLineItem'

import { RRule } from 'rrule'
import statusMixin from '../../../calendar/partials/mixins/statusMixin'
import { CLIENT_REMINDER_LIMIT, STAFF_REMINDER_LIMIT, UNSCHEDULED_REMINDER_LIMIT } from 'components/app/calendar/partials/mixins/remindersMixin'

export default {
  components: {
    EditableOffsetList, DefaultLineItem
  },
  mixins: [statusMixin],
  emits: ['close', 'saved'],
  data () {
    return {
      CLIENT_REMINDER_LIMIT,
      STAFF_REMINDER_LIMIT,
      UNSCHEDULED_REMINDER_LIMIT,
      shorthandNameLimit: 10,
      nameSpacing: 210,
      dialogOpen: false,
      editObject: null,
      errors: {},
      autoShorthandName: false
    }
  },
  computed: {
    mode () {
      return this.editObject && this.editObject.id ? 'edit' : 'add'
    },
    recurringRule () {
      try {
        return RRule.fromString(this.editObject.recursion_rule.value)
      }
      catch (err) {
        return null
      }
    },
    availableRecurringStatusOptions () {
      return this.baseStatusOptions(true).filter(item =>
        item.value === 'SCHEDULED' || item.value === 'UNSCHEDULED' || item.value === 'UNCONFIRMED'
      )
    }
  },
  methods: {
    open (type) {
      this.dialogOpen = true
      this.errors = {}
      this.editObject = JSON.parse(JSON.stringify(type))

      this.$nextTick(() => {
        this.autoShorthandName = this.mode === 'add'
      })
    },
    async saveType () {
      this.errors = {}
      this.$loading.show()
      const { status, payload, errors } = await this.$post('v1/appointments/types/save', {
        ...this.editObject,
        recursion_rule: this.editObject.recursion_rule ? this.editObject.recursion_rule.value : null
      })
      this.$loading.hide()
      if (status === 'success') {
        this.$q.notify({
          color: 'green',
          message: 'Appointment Type successfully saved'
        })
        this.$emit('saved', payload.default)
        this.dialogOpen = false
      }
      else {
        this.errors = errors
        this.$nextTick(() => { this.scrollToFirstError(this.$el) })
      }
    },
    addDataField () {
      this.$refs.dialog.scrollBottom()
      this.editObject.inventory.push({
        description: null,
        unit_price: 0,
        quantity: 1,
        discount_rate: 0,
        tax_type: this.$store.getters['company/getCompany'].default_gst,
        item_id: null,
        inventory_item: null
      })
    },
    removeDataField (key) {
      this.editObject.inventory.splice(key, 1)
    },
    onSaveRRule (str) {
      if (!this.editObject.recursion_rule) {
        this.editObject.recursion_rule = {
          value: str
        }
      }
      else {
        this.editObject.recursion_rule.value = str
      }
    },
    onInputName (val) {
      if (!this.autoShorthandName) {
        return
      }
      // get first letter of each word, concat, set to uppercase
      const str = ((val || '').match(/\b(\w)/g) || []).slice(0, this.shorthandNameLimit).join('').toUpperCase()
      if (str.length < 1) {
        return
      }
      this.editObject.shorthand_name = str
    }
  }
}
</script>
